import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { SupportTicket, SupportTicketStatus, TicketPriority } from '@/types';
import { Button } from '@/components/ui/Button';
import { X, LifeBuoy } from 'lucide-react';
import { API_BASE_URL } from '@/config';
import { SupportTicketForm } from './SupportWidget'; // Import the new reusable form
import mockSupportData from '@/../public/support-tickets.json';

interface MySupportTicketsProps {
    onNavigate: (view: string) => void;
}

// REPLACEMENT MODAL: Uses the new, standardized SupportTicketForm
const CreateTicketModal = ({ onClose, onSubmitSuccess }) => {
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center animate-in fade-in">
            <div className="bg-kala-900 border border-kala-700 rounded-2xl shadow-2xl overflow-hidden w-[400px]">
                <div className="bg-kala-800 p-4 border-b border-kala-700 flex justify-between items-center">
                    <h3 className="font-bold text-white flex items-center gap-2">
                        <LifeBuoy className="w-5 h-5 text-kala-secondary" /> Create Support Ticket
                    </h3>
                    <button onClick={onClose} className="text-kala-400 hover:text-white">
                        <X size={20} />
                    </button>
                </div>
                <SupportTicketForm onSubmitSuccess={onSubmitSuccess} />
            </div>
        </div>
    );
};

const MySupportTickets: React.FC<MySupportTicketsProps> = ({ onNavigate }) => {
  const { currentUser, token } = useAuth();
  const [myTickets, setMyTickets] = useState<SupportTicket[]>([]);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTickets = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    if (!currentUser || !token) {
      // Fallback to mock data for users not logged in
      const userMockTickets = mockSupportData.filter(ticket => ticket.user.email === 'john.doe@example.com');
      setMyTickets(userMockTickets as SupportTicket[]);
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/support/mine`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || 'Failed to fetch support tickets.');
      }

      const responseBody = await response.text();
      const userTickets = responseBody ? JSON.parse(responseBody) : [];
      setMyTickets(userTickets);

    } catch (err: any) {
      setError(err.message + " Falling back to mock data.");
      if (currentUser) {
        const userMockTickets = mockSupportData.filter(
          (ticket) => ticket.user.email === currentUser.email
        );
        setMyTickets(userMockTickets as SupportTicket[]);
      } else {
        setMyTickets([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, token]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleCreateTicket = () => {
    setCreateModalOpen(true);
  };

  const handleCloseModalAndRefresh = () => {
      setCreateModalOpen(false);
      fetchTickets();
  }

  const getStatusClass = (status: SupportTicketStatus) => {
    switch (status?.toUpperCase()) {
      case SupportTicketStatus.OPEN: return 'bg-green-500/20 text-green-300';
      case SupportTicketStatus.IN_PROGRESS: return 'bg-blue-500/20 text-blue-300';
      case SupportTicketStatus.RESOLVED: return 'bg-purple-500/20 text-purple-300';
      case SupportTicketStatus.CLOSED: return 'bg-gray-500/20 text-gray-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };

  const getPriorityClass = (priority: SupportTicketPriority) => {
    switch (priority?.toUpperCase()) {
      case TicketPriority.URGENT: return 'text-red-500';
      case TicketPriority.HIGH: return 'text-red-400';
      case TicketPriority.MEDIUM: return 'text-yellow-400';
      case TicketPriority.LOW: return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <>
        {isCreateModalOpen && <CreateTicketModal onClose={() => setCreateModalOpen(false)} onSubmitSuccess={handleCloseModalAndRefresh} />}
        
        <div className="bg-kala-900 text-white p-8 rounded-lg shadow-lg animate-in fade-in">
        <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold tracking-tight">My Support Tickets</h1>
            <div className="flex items-center space-x-4">
                <Button onClick={handleCreateTicket}>Create New Ticket</Button>
                <button 
                    onClick={() => onNavigate('/dashboard')}
                    className="bg-kala-secondary hover:bg-cyan-400 text-kala-900 font-bold py-2 px-4 rounded"
                >
                    Back to Dashboard
                </button>
            </div>
        </div>
        
        {isLoading ? (
            <div className="text-center py-12 text-kala-400">Loading tickets...</div>
        ) : error ? (
            <div className="text-center py-12 border-2 border-dashed border-red-500/50 rounded-lg bg-red-900/20">
                <p className="text-red-300 font-bold">An error occurred while fetching your tickets.</p>
                <p className="text-red-400 text-sm mt-2">{error}</p>
            </div>
        ) : myTickets.length > 0 ? (
            <div className="space-y-4">
            {myTickets.map((ticket) => (
                <div key={ticket.id} className="bg-kala-800 border border-kala-700 p-5 rounded-xl transition-all hover:border-kala-secondary">
                <div className="flex justify-between items-start">
                    <div>
                    <p className="text-lg font-semibold text-white">{ticket.subject}</p>
                    <p className="text-sm text-kala-400 mt-1">Last updated: {new Date(ticket.lastUpdate || ticket.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${getStatusClass(ticket.status)}`}>
                            {ticket.status}
                        </span>
                        <p className={`text-sm font-bold mt-2 ${getPriorityClass(ticket.priority)}`}>
                            {ticket.priority} Priority
                        </p>
                    </div>
                </div>
                </div>
            ))}
            </div>
        ) : (
            <div className="text-center py-12 border-2 border-dashed border-kala-700 rounded-lg">
                <p className="text-kala-400">You haven't submitted any support tickets yet.</p>
                <Button onClick={handleCreateTicket} className="mt-4">Create Your First Ticket</Button>
            </div>
        )}
        </div>
    </>
  );
};

export default MySupportTickets;
